"use client";
import { useLocale } from "@/lib/locale-context";

import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

// Tiny circular "?" with a centered modal popup containing arbitrary
// HTML. Used on /booking, /booking/confirm and /conditions next to
// services / equipment with a configured description. The popup is a
// portal + backdrop so it looks the same on desktop and mobile.
export function InfoTooltip({ html, title }: { html: string; title?: string }) {
  const { locale } = useLocale();
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  if (!html || !html.trim()) return null;

  const popup = (
    <div
      onClick={() => setOpen(false)}
      style={{
        position: "fixed", inset: 0, zIndex: 2000,
        background: "rgba(15, 18, 26, 0.55)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
        animation: "info-pop-fade 160ms ease-out",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff", borderRadius: 18,
          maxWidth: 540, width: "100%", maxHeight: "85vh", overflow: "auto",
          boxShadow: "0 24px 60px rgba(15, 18, 26, 0.30)",
          padding: 0, position: "relative",
          animation: "info-pop-slide 220ms cubic-bezier(.2,.9,.3,1.05)",
        }}
      >
        <div style={{
          padding: "20px 24px 14px",
          borderBottom: "1px solid #F0F0F5",
          display: "flex", alignItems: "flex-start", gap: 12,
          background: "linear-gradient(180deg,#F2F6FF 0%,#fff 100%)",
          borderRadius: "18px 18px 0 0",
        }}>
          <span style={{
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            width: 32, height: 32, borderRadius: "50%",
            background: "#0039A6", color: "#fff",
            fontSize: 16, fontWeight: 700, flexShrink: 0,
          }}>i</span>
          <div style={{ flex: 1, paddingTop: 4 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#1E1E2D", lineHeight: 1.3 }}>
              {title || (locale === "en" ? "Details" : "Подробнее")}
            </div>
          </div>
          <button
            aria-label="close"
            onClick={() => setOpen(false)}
            style={{
              background: "#F5F5F7", border: "none", cursor: "pointer",
              width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: 10, flexShrink: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1E1E2D" strokeWidth="2.4" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
        <div
          className="info-tooltip-body"
          style={{ fontSize: 15, lineHeight: 1.6, color: "#2B2F33", padding: "16px 24px 22px" }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
        <style>{`
          @keyframes info-pop-fade { from { opacity: 0; } to { opacity: 1; } }
          @keyframes info-pop-slide { from { transform: translateY(20px) scale(.96); opacity: 0; } to { transform: none; opacity: 1; } }
          .info-tooltip-body p { margin: 0 0 10px; }
          .info-tooltip-body p:last-child { margin-bottom: 0; }
          .info-tooltip-body ul, .info-tooltip-body ol { margin: 0 0 10px; padding-left: 22px; }
          .info-tooltip-body li { margin-bottom: 4px; }
          .info-tooltip-body strong { color: #1E1E2D; }
          .info-tooltip-body a { color: #0039A6; text-decoration: underline; }
          .info-tooltip-body img { max-width: 100%; border-radius: 8px; }
        `}</style>
      </div>
    </div>
  );

  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        aria-label={locale === "en" ? "Details" : "Подробнее"}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 18, height: 18, borderRadius: "50%",
          background: "#E0EAFB", color: "#0039A6",
          border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700,
          lineHeight: 1, marginLeft: 6, padding: 0, flexShrink: 0,
          transition: "background 0.15s ease, transform 0.1s ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "#C9DBF7"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "#E0EAFB"; }}
        onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.92)"; }}
        onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      >
        ?
      </button>
      {mounted && open ? createPortal(popup, document.body) : null}
    </>
  );
}
